import React from "react";
import styled, { css } from "styled-components";
import { narrowScreenMediaQuery } from "../../constants/styles";
import { unmodule } from "../../util";
import { AffiliatorMyInfoContext } from "../../providers/ProvideAffiliatorMyInfo";
import useForm, { FAILED, SUBMITTING, SUCCEED } from "../../hooks/useForm";
import Alert from "../../components/Alert";

const MyInfoForm = (props) => {
  const { myInfo, setMyInfo } = React.use(AffiliatorMyInfoContext);
  const [showMyInfo, setShowMyInfo] = React.useState(false);
  const toggleShowMyInfo = React.useCallback(() => {
    setShowMyInfo((x) => !x);
  }, []);

  const canInput = React.useMemo(
    () => showMyInfo || !myInfo.referral_code,
    [showMyInfo, myInfo],
  );
  const showMyInfoInputProps = React.useMemo(
    () =>
      canInput
        ? {
            type: "text",
            disabled: false,
          }
        : {
            type: "password",
            disabled: true,
          },
    [canInput],
  );

  const initialState = React.useCallback(
    () => ({
      bank_name: myInfo.bank_name || "",
      bank_account_number: myInfo.bank_account_number || "",
      bank_account_holder: myInfo.bank_account_holder || "",
    }),
    [myInfo],
  );
  const { inputParams, handleSubmit, submitState } = useForm(
    {
      initialState: initialState,
      url: "/api/v1/users/affiliators/info",
      handleSuccess: ({ parsedResponse, statusCode }) => {
        if (statusCode === 200) {
          setTimeout(() => setShowMyInfo(false), 2000);
          setMyInfo(parsedResponse.data);
        }
      },
    },
    [myInfo],
  );

  return (
    <form onSubmit={handleSubmit}>
      <MyInfoTitleAction onClick={toggleShowMyInfo}>
        My Info
        <EyeIcon
          src={
            canInput
              ? unmodule(require("../../images/icons/eye.svg"))
              : unmodule(require("../../images/icons/eye-slash.svg"))
          }
        />
      </MyInfoTitleAction>
      {!myInfo.referral_code && (
        <Alert $info>
          Lengkapi data dibawah untuk bergabung dengan affiliator
        </Alert>
      )}
      <BankInfoFlex>
        <BankInfoFieldItem>
          <BankInfoFieldName>Bank</BankInfoFieldName>
          <BankInfoFieldInput
            required
            {...inputParams("bank_name")}
            {...showMyInfoInputProps}
          />
        </BankInfoFieldItem>
        <BankInfoFieldItem>
          <BankInfoFieldName>No. Rekening</BankInfoFieldName>
          <BankInfoFieldInput
            required
            {...inputParams("bank_account_number")}
            {...showMyInfoInputProps}
          />
        </BankInfoFieldItem>
      </BankInfoFlex>
      <BankInfoFieldItem>
        <BankInfoFieldName>Nama Pemilik</BankInfoFieldName>
        <BankInfoFieldInput
          required
          {...inputParams("bank_account_holder")}
          {...showMyInfoInputProps}
        />
      </BankInfoFieldItem>
      {canInput && (
        <MyInfoFormSubmitDiv>
          {submitState === SUBMITTING ? (
            <SubmitButton disabled type="button" $submitting>
              Loading...
            </SubmitButton>
          ) : submitState === SUCCEED ? (
            <SubmitButton disabled type="button" $succeed>
              Berhasil!
            </SubmitButton>
          ) : submitState === FAILED ? (
            <SubmitButton disabled type="button" $failed>
              Gagal
            </SubmitButton>
          ) : (
            <SubmitButton type="submit">Simpan</SubmitButton>
          )}
        </MyInfoFormSubmitDiv>
      )}
    </form>
  );
};

const CAN_SHARE = false;

const UnnamedComponent = (props) => {
  const { myInfo } = React.use(AffiliatorMyInfoContext);
  const [copied, setCopied] = React.useState(false);
  const copiedTimeoutIdRef = React.useRef();
  const copyReferralCode = React.useCallback(() => {
    if (copiedTimeoutIdRef.current) {
      clearTimeout(copiedTimeoutIdRef.current);
    }
    setCopied(true);
    copiedTimeoutIdRef.current = setTimeout(() => setCopied(false), 1000);
    (async () => {
      await navigator.clipboard.writeText(myInfo.referral_code);
    })();
  }, [myInfo]);

  return (
    <MyAffInfo>
      <MyInfoForm />
      <InnerSeparator />
      <div>
        <CardTitle>Kode Referral</CardTitle>
        <RefCodeInner onClick={copyReferralCode}>
          <CodeNumber>{myInfo.referral_code}</CodeNumber>
          {myInfo.referral_code ? (
            <CopyCodeDiv $copied={copied}>
              <img src={unmodule(require("../../images/icons/copy.svg"))} />
              {copied ? <span>copied!</span> : <span>copy</span>}
            </CopyCodeDiv>
          ) : (
            <React.Fragment>
              <div>
                lengkapi <b>My Info</b> terlebih dahulu
              </div>
              <div></div>
            </React.Fragment>
          )}
        </RefCodeInner>
        {CAN_SHARE && (
          <RefCodeShare>
            <img src={unmodule(require("../../images/icons/share.svg"))} />
            Share
          </RefCodeShare>
        )}
      </div>
      <InnerSeparator />
      <div>
        <CardTitle>Saldo</CardTitle>
        <CardMetric>{myInfo.balance || "-"}</CardMetric>
      </div>
    </MyAffInfo>
  );
};

export default UnnamedComponent;

const Card = styled.div`
  background-color: #2e2e33;
  border-radius: 5px;
  box-shadow: 0px 0px 1px rgb(255 255 255);
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: space-between;
  padding: 20px;
`;

const CardTitle = styled.div`
  font-weight: bold;
  margin-bottom: 15px;
`;

const MyAffInfo = styled(Card)`
  grid-column: 3 / 4; /* last column */
  grid-row: 1 / span 2; /* take 2 rows */
  justify-content: unset;

  @media ${narrowScreenMediaQuery} {
    grid-column: unset;
  }
`;

const CardMetric = styled.div`
  font-size: 30px;
  font-weight: bold;
`;

const InnerSeparator = styled.div`
  background-color: #434343;
  height: 1px;
  margin-left: -20px;
  width: calc(100% + 40px);
`;

const RefCodeInner = styled.div`
  // color: #dd5713;
  align-items: center;
  background-color: #434343;
  border-radius: 5px;
  border: 1px solid #616161;
  display: flex;
  justify-content: space-between;
  padding: 8px 10px;
  position: relative;

  &:hover,
  &:focus {
    filter: brightness(0.95);
    cursor: pointer;
  }
`;

const CodeNumber = styled.div`
  letter-spacing: 5px;
`;

const CopyCodeDiv = styled.div`
  align-items: center;
  color: white;
  display: flex;

  & > span {
    font-weight: bold;
    margin-left: 5px;
  }

  ${(props) =>
    props.$copied &&
    css`
      & > span {
        font-weight: normal;
      }
    `};
`;

const RefCodeShare = styled.button`
  align-items: center;
  background-color: #0077cc;
  border-radius: 2px;
  color: inherit;
  display: flex;
  font-weight: bold;
  gap: 5px;
  justify-content: center;
  margin-top: 5px;
  padding: 10px 0;
  width: 100%;
  border: none;

  &:hover,
  &:active {
    cursor: pointer;
    transform: translate(1px, 1px); /* Simple press effect */
  }
`;

const BankInfoFlex = styled.div`
  display: flex;
  gap: 5px;
  margin-bottom: 20px;
`;

const BankInfoFieldItem = styled.div`
  position: relative;

  ${BankInfoFlex} > &:first-child {
    flex: 1 1 0;
  }

  ${BankInfoFlex} > &:last-child {
    flex: 2 1 0;
  }
`;

const BankInfoFieldName = styled.div`
  background-color: #2e2e33;
  color: #cfcfcf;
  font-size: 12px;
  left: 4px;
  padding: 0 4px;
  position: absolute;
  top: -9px;
`;

const BankInfoFieldInput = styled.input`
  background-color: #2e2e33;
  border-radius: 4px;
  border: 1px solid #6d6d6d;
  color: white;
  font-size: 14px;
  padding: 10px;
  width: calc(100% - 25px);
`;

const MyInfoTitleAction = styled(CardTitle)`
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  margin-bottom: 20px;

  &:hover {
    text-decoration: underline;
  }
`;

const EyeIcon = styled.img`
  width: 20px;
  margin-left: 5px;
`;

const MyInfoFormSubmitDiv = styled.div`
  margin-top: 10px;
  text-align: right;
`;

const SubmitButton = styled.button`
  background-color: #f44336;
  border-radius: 4px;
  border: none;
  color: white;
  font-weight: bold;
  padding: 8px 10px;

  &:hover,
  &:active {
    cursor: pointer;
    transform: translate(1px, 1px);
  }

  ${(props) =>
    props.$succeed &&
    css`
      background-color: green;
      &:hover,
      &:active {
        cursor: not-allowed;
        transform: unset;
      }
    `};

  ${(props) =>
    props.$failed &&
    css`
      background-color: red;
      &:hover,
      &:active {
        cursor: not-allowed;
        transform: unset;
      }
    `};

  ${(props) =>
    props.$submitting &&
    css`
      background-color: grey;
      &:hover,
      &:active {
        cursor: not-allowed;
        transform: unset;
      }
    `};
`;
